import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Icon, Skeleton, Tag, Typography } from '@ann/web-components';
import {
  WidgetGrid,
  WidgetGridProvider,
  WidgetCardShell,
} from '../../../../../components/WidgetGrid';
import ActivityListWidget from '../../../../../components/WidgetGrid/customWidgets/ucpWidgets/ActivityListWidget';
import NotesListWidget from '../../../../../components/WidgetGrid/customWidgets/ucpWidgets/NotesListWidget';
import Widget from '../../../../../components/WidgetGrid/components/Widget/Widget';
import { ShareDashboardModal } from '../../components';
import { DASHBOARD_STATES } from '../../constants';
import WIDGET_PREVIEW_DATA from '../../constants/widgetPreviewData';
import { useDashboardEditor } from '../../hooks';
import { adaptWidgetsForGrid } from '../../utils/widgetAdapter';
import {
  CanvasWrapper,
  Container,
  DescriptionText,
  EmptyStateCard,
  EmptyStateContent,
  EmptyStateDescription,
  EmptyStateTitle,
  Header,
  HeaderActionsRow,
  HeaderLeft,
  HeaderRight,
  HeaderTopRow,
  IconHighlight,
  LocationPill,
  TitleBlock,
  TitleRow,
  TitleText,
} from './DashboardDetail.styled';
import { getDashboardById } from '../../../../../services/dashboardBuilder';
import { DASHBOARD_DETAIL_COPY, DASHBOARD_EDITOR_COPY } from '../../constants/dashboardBuilderCopy';

const PREVIEW_REGISTRY = {
  activity: ActivityListWidget,
  notes: NotesListWidget,
};

const DashboardDetailSkeleton = () => (
  <Container>
    <Skeleton height="56px" width="100%" borderRadius="8px" />
    <Skeleton height="20px" width="360px" borderRadius="4px" />
    <Skeleton height="200px" width="100%" borderRadius="8px" />
  </Container>
);

const DashboardDetail = () => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [toast, setToast] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const {
    error,
    id,
    loading,
    sharedWith,
    state,
    updateEditor,
    widgets,
  } = useDashboardEditor();

  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      setIsLoadingDashboard(true);
      try {
        const response = await getDashboardById(dashboardId);
        if (!isMounted) return;
        setDashboard(response?.body || null);
      } catch (err) {
        if (!isMounted) return;
        setDashboard(null);
      } finally {
        if (!isMounted) return;
        setIsLoadingDashboard(false);
      }
    };

    loadDashboard();
    return () => {
      isMounted = false;
    };
  }, [dashboardId]);

  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (!incomingToast) return;
    setToast(incomingToast);
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.state, navigate]);

  const handleBack = useCallback(() => {
    navigate('/dashboards');
  }, [navigate]);

  const handleEdit = useCallback(() => {
    if (!dashboardId) return;
    navigate(`/dashboards/${dashboardId}/edit`);
  }, [dashboardId, navigate]);

  const closeToast = useCallback(() => {
    setToast(null);
    navigate(`/dashboards/${id}/edit`);
  }, [navigate, id]);

  const openShareModal = useCallback(() => setIsShareModalOpen(true), []);
  const closeShareModal = useCallback(() => setIsShareModalOpen(false), []);

  const handleShareConfirm = useCallback(
    (assigned) => {
      updateEditor({ sharedWith: Array.isArray(assigned) ? assigned : [] });
      closeShareModal();
      setToast({
        severity: 'success',
        title: DASHBOARD_EDITOR_COPY.toast.shared,
        description: DASHBOARD_EDITOR_COPY.toast.sharedDescription,
        autoClose: true,
      });
    },
    [closeShareModal, updateEditor],
  );

  const statusLabel = useMemo(
    () => (state === DASHBOARD_STATES.PUBLISHED ? 'Published' : 'Draft'),
    [state],
  );

  const hasWidgets = useMemo(() => widgets.length > 0, [widgets.length]);

  const externalWidgetConfig = useMemo(
    () => adaptWidgetsForGrid(widgets),
    [widgets],
  );

  const renderWidget = useCallback((widgetConfig) => {
    const builderType = widgetConfig._builderType;
    const previewData = WIDGET_PREVIEW_DATA[builderType] ?? null;
    const cardConfig = widgetConfig.config?.card || {};
    const WidgetComponent = PREVIEW_REGISTRY[builderType];

    let content = null;
    if (previewData && WidgetComponent) {
      content = <WidgetComponent data={previewData} isCompressed={false} />;
    } else if (previewData) {
      content = <Widget data={previewData} />;
    }

    return (
      <WidgetCardShell
        title={cardConfig.title}
        headerButtons={cardConfig.header_buttons}
      >
        {content}
      </WidgetCardShell>
    );
  }, []);

  if (loading || isLoadingDashboard) {
    return <DashboardDetailSkeleton />;
  }

  const title = dashboard?.title || 'Untitled Dashboard';
  const description = dashboard?.description || '';
  const sectionPath = dashboard?.sectionPath || '';
  const isPublished = state === DASHBOARD_STATES.PUBLISHED;

  if (error) {
    return (
      <Container>
        <EmptyStateCard>
          <EmptyStateContent>
            <IconHighlight>
              <Icon name="dashboard" size="small" color="neutral" />
            </IconHighlight>
            <EmptyStateTitle>Dashboard not found</EmptyStateTitle>
            <EmptyStateDescription>
              This dashboard does not exist or you may not have access to it.
            </EmptyStateDescription>
          </EmptyStateContent>
        </EmptyStateCard>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Header>
          <HeaderTopRow>
            <HeaderLeft>
              <Button
                type="tertiary"
                iconLeft="arrowLeft"
                size="small"
                onClick={handleBack}
              />
              <TitleBlock>
                <TitleRow>
                  <TitleText title={title}>{title}</TitleText>
                  <Tag
                    text={statusLabel}
                    color={isPublished ? 'success' : 'neutral'}
                    icon={isPublished ? 'check' : undefined}
                    size="small"
                  />
                  {sectionPath && (
                    <LocationPill>
                      <Icon name="locationOn" size="small" color="neutral" />
                      <Typography
                        category="body"
                        size="small"
                        weight="regular"
                        color="neutral"
                      >
                        {sectionPath}
                      </Typography>
                    </LocationPill>
                  )}
                </TitleRow>
                {description && <DescriptionText>{description}</DescriptionText>}
              </TitleBlock>
            </HeaderLeft>
            <HeaderRight>
              <Button
                type="tertiary"
                size="small"
                iconLeft="share"
                onClick={openShareModal}
              />
            </HeaderRight>
          </HeaderTopRow>

          <HeaderActionsRow>
            <Button
              type="primary"
              size="small"
              iconLeft="edit"
              label={DASHBOARD_DETAIL_COPY.editButton}
              onClick={handleEdit}
            />
          </HeaderActionsRow>
        </Header>

        {hasWidgets ? (
          <CanvasWrapper>
            <WidgetGridProvider isEditMode={false}>
              <WidgetGrid
                externalWidgetConfig={externalWidgetConfig}
                renderWidget={renderWidget}
                readOnly
              />
            </WidgetGridProvider>
          </CanvasWrapper>
        ) : (
          <EmptyStateCard>
            <EmptyStateContent>
              <IconHighlight>
                <Icon name="dashboard" size="small" color="neutral" />
              </IconHighlight>
              <EmptyStateTitle>No widgets</EmptyStateTitle>
              <EmptyStateDescription>
                This dashboard has no widgets configured yet.
              </EmptyStateDescription>
            </EmptyStateContent>
          </EmptyStateCard>
        )}
      </Container>

      {toast && (
          <Alert
            severity={toast.severity}
            title={toast.title}
            description={toast.description}
            zIndex={1300}
            closable
            autoClose={toast.autoClose}
            autoCloseDelay={4000}
            onClose={closeToast}
            maxWidth="692px"
          />
      )}

      {isShareModalOpen && (
        <ShareDashboardModal
          open={isShareModalOpen}
          existingSharedWith={sharedWith}
          dashboardName={title}
          onClose={closeShareModal}
          onConfirm={handleShareConfirm}
        />
      )}
    </>
  );
};

export default DashboardDetail;
